import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Dropdown, message } from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SendOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Input, Select, DatePicker } from 'antd';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  getRegulatoryReports,
  exportRegulatoryReport,
  submitReportToRegulator,
} from '../../services/regulatoryReportingService';
import ReportDetailModal from './ReportDetailModal';

const { Option } = Select;
const { RangePicker } = DatePicker;

const STATUS_CONFIG = {
  DRAFT: { color: 'default', label: 'Draft' },
  PENDING_APPROVAL: { color: 'gold', label: 'Pending Approval' },
  APPROVED: { color: 'green', label: 'Approved' },
  REJECTED: { color: 'red', label: 'Rejected' },
  SUBMITTED: { color: 'blue', label: 'Submitted' },
  ARCHIVED: { color: 'purple', label: 'Archived' },
};

const REPORT_TYPE_LABELS = {
  CBN_RETURNS: 'CBN Returns',
  NDIC_RETURNS: 'NDIC Returns',
  AML_CTR: 'AML — CTR',
  AML_SAR: 'AML — SAR',
  AML_STR: 'AML — STR',
  NFIU_REPORT: 'NFIU Report',
  EFCC_REPORT: 'EFCC Compliance',
  CREDIT_BUREAU: 'Credit Bureau',
  CAPITAL_ADEQUACY: 'Capital Adequacy',
  LIQUIDITY_REPORT: 'Liquidity Report',
  PRUDENTIAL_RETURNS: 'Prudential Returns',
  RISK_ASSESSMENT: 'Risk Assessment',
  KYC_COMPLIANCE: 'KYC Compliance',
  TRANSACTION_MONITORING: 'Transaction Monitoring',
  CUSTOM: 'Custom',
};

const DEFAULT_FILTERS = {
  search: '',
  reportType: null,
  status: null,
  startDate: null,
  endDate: null,
};

const ReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedReport, setSelectedReport] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchReports = useCallback(async (page = 1, size = 10, f = appliedFilters) => {
    setLoading(true);
    try {
      const res = await getRegulatoryReports({ page: page - 1, size, ...f });
      if (res?.status) {
        setReports(res.data?.content || []);
        setPagination({ current: page, pageSize: size, total: res.data?.totalElements || 0 });
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleTableChange = (pag) => fetchReports(pag.current, pag.pageSize);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchReports(1, pagination.pageSize, filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    fetchReports(1, pagination.pageSize, DEFAULT_FILTERS);
  };

  const handleChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters({
        ...filters,
        startDate: dates[0]?.format('YYYY-MM-DD'),
        endDate: dates[1]?.format('YYYY-MM-DD'),
      });
    } else {
      setFilters({ ...filters, startDate: null, endDate: null });
    }
  };

  const handleRowClick = (record) => {
    setSelectedReport(record);
    setModalVisible(true);
  };

  const handleSubmit = async (e, record) => {
    e.stopPropagation();
    try {
      const res = await submitReportToRegulator(record.id);
      if (res?.status) {
        message.success('Report submitted successfully');
        fetchReports(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || 'Failed to submit');
      }
    } catch (err) {
      message.error('Submission failed');
    }
  };

  const handleExportSingle = async (e, record, format) => {
    e.stopPropagation();
    try {
      const res = await exportRegulatoryReport(record.id, { format });
      if (res?.status && res.data) {
        const data = Array.isArray(res.data) ? res.data : [res.data];
        const ws = XLSX.utils.json_to_sheet(data);

        if (format === 'csv') {
          const csvOutput = XLSX.utils.sheet_to_csv(ws);
          const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
          saveAs(blob, `${record.reportName || 'report'}_${moment().format('YYYY-MM-DD')}.csv`);
          message.success('CSV exported successfully');
        } else {
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Report');
          const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, `${record.reportName || 'report'}_${moment().format('YYYY-MM-DD')}.xlsx`);
          message.success('Excel exported successfully');
        }
      } else {
        message.warning('No data to export');
      }
    } catch (err) {
      message.error('Export failed');
    }
  };

  const columns = [
    {
      title: 'Report Name',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</span>,
    },
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'reportType',
      render: (type) => (
        <Tag
          style={{
            background: '#f5f5f5',
            color: '#595959',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            fontWeight: 500,
          }}
        >
          {REPORT_TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Reporting Period',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <span style={{ color: '#595959', fontSize: 12 }}>
          {record.startDate && record.endDate
            ? `${moment(record.startDate).format('MMM DD')} — ${moment(record.endDate).format('MMM DD, YYYY')}`
            : '—'}
        </span>
      ),
    },
    {
      title: 'Generated',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      width: 170,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || { color: 'default', label: status };
        return (
          <Tag color={cfg.color} className="reg-status-tag">
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <div className="reg-action-row" onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            style={{ borderRadius: 6, fontSize: 11 }}
            onClick={() => handleRowClick(record)}
          />
          <Dropdown
            menu={{
              items: [
                { key: 'csv', label: 'Export CSV', icon: <DownloadOutlined />, onClick: (e) => handleExportSingle(e.domEvent, record, 'csv') },
                { key: 'excel', label: 'Export Excel', icon: <FileExcelOutlined />, onClick: (e) => handleExportSingle(e.domEvent, record, 'excel') },
              ],
            }}
            trigger={['click']}
          >
            <Button size="small" icon={<DownloadOutlined />} style={{ borderRadius: 6, fontSize: 11 }} />
          </Dropdown>
          {record.status === 'APPROVED' && (
            <Button
              size="small"
              icon={<SendOutlined />}
              className="reg-action-submit"
              style={{ height: 24, fontSize: 11, padding: '0 8px' }}
              onClick={(e) => handleSubmit(e, record)}
            >
              Submit
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* ── Filter Bar ──────────────────────────────── */}
      <div className="reg-filter-bar">
        <div className="reg-filter-row">
          <div className="reg-filter-item filter-search">
            <span className="reg-filter-label">Search</span>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Report name, type..."
              allowClear
              value={filters.search}
              onChange={(e) => handleChange('search', e.target.value)}
            />
          </div>
          <div className="reg-filter-item">
            <span className="reg-filter-label">Report Type</span>
            <Select
              placeholder="All Types"
              allowClear
              value={filters.reportType || undefined}
              onChange={(val) => handleChange('reportType', val)}
            >
              {Object.entries(REPORT_TYPE_LABELS).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
          </div>
          <div className="reg-filter-item">
            <span className="reg-filter-label">Status</span>
            <Select
              placeholder="All Statuses"
              allowClear
              value={filters.status || undefined}
              onChange={(val) => handleChange('status', val)}
            >
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <Option key={key} value={key}>{cfg.label}</Option>
              ))}
            </Select>
          </div>
          <div className="reg-filter-item">
            <span className="reg-filter-label">Date Range</span>
            <RangePicker onChange={handleDateChange} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </div>
          <div className="reg-filter-actions">
            <Button icon={<FilterOutlined />} onClick={handleApplyFilters} className="reg-filter-apply-btn">
              Filter
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilters} className="reg-filter-reset-btn">
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* ── Table ──────────────────────────────── */}
      <div className="reg-table-card">
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} reports`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'reg-row-even' : 'reg-row-odd')}
          className="reg-table"
        />
      </div>

      {/* ── Detail Modal ──────────────────────────────── */}
      <ReportDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedReport}
        onRefresh={() => fetchReports(pagination.current, pagination.pageSize)}
      />
    </div>
  );
};

export default ReportsTab;
